import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { TimePickerIcon } from '../icons';

export const TimePickerBase = ({ onChange, value, hasError, helpText }) => {
  const handleChange = (val) => {
    if (onChange) {
      const minutes = val ? val.diff(moment().startOf('day'), 'minutes') : 0;
      onChange(minutes);
    }
  };

  const formattedValue = useMemo(() => {
    return moment.isMoment(value)
      ? value
      : moment().startOf('day').add(value, 'minutes');
  }, [value]);

  return (
    <TimePicker
      reduceAnimations
      value={formattedValue}
      formatDensity="dense"
      onChange={handleChange}
      slotProps={{
        textField: {
          error: !!hasError,
          sx: {
            '& .MuiInputBase-root': {
              backgroundColor: 'input.background',
            },
            input: {
              padding: '6px 12px',
              '&::placeholder': {
                color: 'text.subtle',
                opacity: 1,
              },
            },
          },
        },
        openPickerIcon: {
          sx: {
            color: 'text.secondary',
          },
        },
      }}
      slots={{
        openPickerIcon: TimePickerIcon,
      }}
      margin="none"
    />
  );
};

const Timepicker = ({
  label,
  className,
  hasError,
  helpText,
  onChange,
  value,
  size,
  margin = 'normal',
  ...other
}) => {
  return (
    <FormControl
      error={hasError}
      className={className}
      margin={margin}
      {...other}
    >
      {label && <FormLabel sx={{ mb: 1 }}>{label}</FormLabel>}
      <TimePickerBase
        value={value}
        onChange={onChange}
        hasError={hasError}
        helpText={helpText}
      />
      {helpText && <FormHelperText>{helpText}</FormHelperText>}
    </FormControl>
  );
};

export const getMinutes = (data) => {
  if (!data) return '-1';
  const { h, m } = data;
  if (h < 0) return h;
  const d = moment.tz().startOf('day');
  return d.clone().set('h', h).set('minute', m).diff(d, 'minute');
};

export const getHourMinute = (minutes) => {
  if (minutes < 0) {
    return {
      h: 0,
      m: 0,
    };
  }
  const d = moment.tz().startOf('day').set('minute', minutes);
  return {
    h: +d.format('H'),
    m: +d.format('m'),
  };
};

export const formatMinutes = (minutes) => {
  if (minutes === undefined) return 0;
  return moment().startOf('day').set('minutes', minutes).format('hh:mm A');
};

export const minutesToUTCDate = (minutes) => {
  const timeData = getHourMinute(minutes);
  return moment
    .utc()
    .startOf('day')
    .hour(timeData.h)
    .minute(timeData.m)
    .toDate();
};

export const dateToMinutes = (date) => {
  if (date) {
    const cloned = date.clone().startOf('day');
    return date.diff(cloned, 'minutes');
  }
  return 0;
};

Timepicker.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  helpText: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.oneOf(['dense', 'normal', 'none']),
  disabled: PropTypes.bool,
};

export default Timepicker;
